import React, { useMemo, useState } from 'react';
import Button from '@cashew/button';
import { api, dateFormat, priceFormat } from '@cashew/utils';
import Tag from '@cashew/tag';
import ConfirmModal from '@cashew/confirm-modal';
import { ChargebackData } from './index.types';
import { ResultType } from '../index.types';

const ChargebackItem = ({ data, t }: { data: ChargebackData; t: any }) => {
  const [showResult, setShowResult] = useState(false);
  const [actionType, setActionType] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [resultCode, setResultCode] = useState<ResultType>('none');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [itemStatus, setItemStatus] = useState(data.status || 'DISPUTE');

  const resultDescription = useMemo(
    () => (
      <div className="resultDescriptionStatus" style={{ marginTop: 16 }}>
        {t(`chargeback-action.result.description.${resultCode}`, {
          actionType,
        })}
      </div>
    ),
    [resultCode]
  );

  const modalDescription = useMemo(
    () =>
      isConfirmationModalOpen
        ? 'Are you sure you want to log this chargeback?'
        : 'This action cannot be reversed',
    [isConfirmationModalOpen]
  );

  const onConfirm = () => {
    setIsSubmitting(true);
    api
      .patch({
        endpoint: `payments/chargeback/${data.id}/${actionType}`,
      })
      .then(() => {
        setItemStatus(actionType.toUpperCase());
        setResultCode('success');
        window.location.reload();
      })
      .catch((error) => {
        setResultCode(error?.response?.data?.errorCode);
      })
      .finally(() => {
        setShowResult(true);
        setIsSubmitting(false);
      });
  };

  const confirmButtons = (
    <div style={{ display: 'flex' }}>
      <Button
        extraClass="btn-black"
        onClick={() => {
          if (isConfirmationModalOpen) {
            setIsConfirmationModalOpen(false);
          } else {
            setResultCode('warning');
            setShowResult(true);
          }
        }}
      >
        {isConfirmationModalOpen ? 'No' : 'Cancel'}
      </Button>
      <Button
        extraClass="order-details__success"
        loading={isSubmitting}
        disabled={isSubmitting}
        onClick={onConfirm}
      >
        {isConfirmationModalOpen ? 'Yes' : t('button.confirm')}
      </Button>
    </div>
  );

  const resultButtons = (
    <div>
      <Button
        extraClass="btn-blue"
        onClick={() => {
          setIsModalOpen(false);
          setIsConfirmationModalOpen(false);
          setShowResult(false);
          setResultCode('none');
        }}
      >
        Ok
      </Button>
    </div>
  );
  return (
    <>
      <div className="chargeback-item">
        <div>
          <p>{dateFormat({ date: data.requestDate })}</p>
        </div>

        <div>
          <p>{dateFormat({ date: data.paymentDate })}</p>
        </div>

        <div>
          <p>{dateFormat({ date: data.paymentDate })}</p>
        </div>

        <div>
          <p>
            {priceFormat({
              currency: 'AED',
              amount: data.amount,
            })}
          </p>
        </div>

        <div>
          <Tag status={itemStatus}>{t(`status.${itemStatus}`)}</Tag>
        </div>

        <div>
          <p>{data.authCode}</p>
        </div>

        <div className="truncate">
          <p>{data.arn}</p>
        </div>

        <div className="chargeback-actions">
          <Button
            disabled={['WON', 'LOST'].includes(itemStatus)}
            onClick={() => {
              setActionType('won');
              setIsModalOpen(true);
            }}
          >
            Won
          </Button>
          <Button
            disabled={['WON', 'LOST'].includes(itemStatus)}
            onClick={() => {
              setActionType('lost');
              setIsModalOpen(true);
            }}
          >
            Lost
          </Button>
        </div>
      </div>

      {isModalOpen && (
        <ConfirmModal
          isVisible={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
          }}
          showCloseButton={false}
          confirmTitle={`Mark this ${priceFormat({
            currency: 'AED',
            amount: data.amount,
          })} chargeback claim as ${actionType}?`}
          confirmDescription={modalDescription}
          confirmButtons={confirmButtons}
          resultTitle={t(`chargeback-action.result.title.${resultCode}`)}
          resultDescriptionComponent={resultDescription}
          resultButtons={resultButtons}
          showResult={showResult}
          resultStatus={
            ['none', 'success', 'warning'].includes(resultCode)
              ? resultCode
              : 'error'
          }
        />
      )}
    </>
  );
};

export default ChargebackItem;
